import { Controller } from "@hotwired/stimulus"

import { fetchPostJson, fetchUrl } from "../api/generic_fetch"
import airbrakeClient from "../helpers/airbrake"
import { CONSENT, isConsentGranted } from "../helpers/consent"
import { isSignedIn, StoredUser } from "../helpers/current_user"
import { pushGa4Event } from "../helpers/ga4_helper"
import { SEVERITY } from "../helpers/logger"
import { localStorage } from "../helpers/storage"
import type NavbarController from "./navbar/navbar_controller"

declare global {
  interface Window {
    google?: typeof google
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client?hl=en"

export default class GoogleOneTapController extends Controller<HTMLElement> {
  static outlets = ["navbar"]
  declare readonly navbarOutlet: NavbarController

  static values = { clientId: String }
  declare readonly clientIdValue: string

  private static gisPromise: Promise<void> | null = null

  static get shouldLoad(): boolean {
    return !isSignedIn() && isConsentGranted(CONSENT.FUNCTIONALITY)
  }

  connect(): void {
    void this.start()
  }

  disconnect(): void {
    try {
      // GIS's cancel() calls removeChild() on the One Tap prompt container. If that
      // container was already detached (e.g. Turbo removed this subtree, or Google
      // cleaned it up), removeChild throws NotFoundError. The teardown is a no-op at
      // that point, so swallow it rather than letting Stimulus report it to Airbrake.
      window.google?.accounts?.id?.cancel()
    } catch {
      // no-op — nothing left to cancel
    }
  }

  private async handleCredential(response: google.accounts.id.CredentialResponse): Promise<void> {
    try {
      const res = await fetchPostJson("/my-account/google-tap-sign-in", {
        credential: response.credential
      })

      if (!res.ok) {
        return
      }

      // Contract: the endpoint returns the signed-in user in the same shape the navbar
      // reads from localStorage["user"] (at least { firstName }).
      const user: StoredUser = await res.json()
      localStorage?.setItemAtUserRequest("user", JSON.stringify(user))

      // Re-render the navbar so it reflects the newly signed-in user without a full page reload.
      this.navbarOutlet.render()

      pushGa4Event({ event: "login", method: "google_one_tap" })
    } catch (error) {
      void airbrakeClient.notify({
        error: "Google One Tap sign-in request failed",
        params: { error }
      })
    }
  }

  private loadGis(): Promise<void> {
    GoogleOneTapController.gisPromise ??= new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve()
        return
      }

      const script = document.createElement("script")
      script.src = GIS_SRC
      script.async = true
      script.addEventListener("load", () => resolve())
      script.addEventListener("error", () => reject(new Error("GIS failed to load")))
      document.head.appendChild(script)
    })

    return GoogleOneTapController.gisPromise
  }

  private notifyNonceError(error: unknown): null {
    void airbrakeClient.notify({
      error: "Google One Tap failed to fetch nonce",
      params: { error },
      context: { severity: SEVERITY.WARNING }
    })
    return null
  }

  private async fetchNonce(): Promise<string | null> {
    try {
      const { nonce } = await fetchUrl("/my-account/google-tap-nonce")
      if (!nonce) {
        return this.notifyNonceError("Nonce missing in response")
      }

      return nonce
    } catch {
      // do not send Airbrake
      return null
    }
  }

  private async start(): Promise<void> {
    try {
      await this.loadGis()
    } catch {
      // GIS commonly fails to load under some Safari privacy settings (the cross-site
      // script is blocked). There's nothing actionable to do, so bail silently.
      return
    }

    if (window.google?.accounts?.id) {
      const nonce = await this.fetchNonce()
      if (!nonce) {
        return
      }

      window.google.accounts.id.initialize({
        client_id: this.clientIdValue,
        callback: (response): void => void this.handleCredential(response),
        // Never sign the user in silently — always require an explicit tap.
        auto_select: false,
        // Tapping/clicking outside the prompt does NOT dismiss it (only the X does).
        cancel_on_tap_outside: false,
        // Enable the upgraded One Tap UX on ITP browsers (Safari, Firefox, Chrome on iOS), where the
        // normal passive prompt is blocked.
        itp_support: true,
        nonce
      })

      window.google.accounts.id.prompt()
    }
  }
}
